import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, TextField, Typography } from '@mui/material';
import FileUploadOutlined from '@mui/icons-material/FileUploadOutlined';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import PersonOutline from '@mui/icons-material/PersonOutline';
import GroupsOutlined from '@mui/icons-material/GroupsOutlined';
import { cvApi, cvError } from '../hooks/cvApi';
import { downloadCvFile, MAX_CV_FILE_BYTES, parseCvFile } from '../cvTransfer';
import { useCvLanguage } from '../cvLanguage';

export default function CvJsonTransfer({ profiles, activeProfileId, contextName, disabled, onExport, onImport, operationError }) {
  const { t } = useCvLanguage();
  const [mode, setMode] = useState(null);
  const [selected, setSelected] = useState([]);
  const [curriculums, setCurriculums] = useState(null);
  const [scope, setScope] = useState(null);
  const [persons, setPersons] = useState([]);
  const [personId, setPersonId] = useState('');
  const [personName, setPersonName] = useState('');
  const [loadingPersons, setLoadingPersons] = useState(false);
  const [peopleError, setPeopleError] = useState('');
  const [peopleAttempt, setPeopleAttempt] = useState(0);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const request = useRef(null);
  const locked = useRef(false);
  const attempted = useRef(false);
  useEffect(() => {
    if (mode !== 'import' || scope !== 'other') return;
    let active = true;
    setLoadingPersons(true); setPeopleError('');
    cvApi.list({scope: 'other'}).then(result => { if (active) setPersons(result.persons || []); }).catch(e => { if (active) setPeopleError(cvError(e)); }).finally(() => { if (active) setLoadingPersons(false); });
    return () => { active = false; };
  }, [mode, scope, peopleAttempt]);
  const open = next => {
    setMode(next); setSelected(activeProfileId ? [activeProfileId] : []); setCurriculums(null); setScope(null); setPersonId(''); setPersonName(''); setError('');
    request.current = null; attempted.current = false;
  };
  const close = () => { if (!locked.current) setMode(null); };
  const readFile = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setError(''); setCurriculums(null); setScope(null); request.current = null;
    if (file.size > MAX_CV_FILE_BYTES) { setError('El archivo supera 5 MB. Exporta menos currículums a la vez.'); return; }
    locked.current = true; setBusy(true);
    try {
      const text = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('No se pudo leer el archivo.'));
        reader.readAsText(file);
      });
      setCurriculums(parseCvFile(text));
    } catch (e) { setError(e.message); }
    finally { locked.current = false; setBusy(false); }
  };
  const submit = async () => {
    if (locked.current) return;
    locked.current = true; setBusy(true); setError('');
    try {
      if (mode === 'export') {
        const documents = await onExport(selected);
        if (!documents) throw new Error('No se pudo completar la operación del currículum');
        downloadCvFile(documents);
      } else {
        if (!request.current) request.current = {
          requestId: crypto.randomUUID(), scope, curriculums,
          ...(scope === 'other' ? personId ? {personId} : {personName: personName.trim()} : {})
        };
        attempted.current = true;
        if (!await onImport(request.current)) throw new Error('No se pudo importar. Reintenta para continuar con la misma operación.');
      }
      setMode(null);
    } catch (e) { setError(cvError(e)); }
    finally { locked.current = false; setBusy(false); }
  };
  const destinationMissing = !scope || (scope === 'other' && (loadingPersons || Boolean(peopleError) || (!personId && !personName.trim())));
  return <>
    <Box className="cv-transfer-actions">
      <Button startIcon={<FileUploadOutlined />} onClick={() => open('import')} disabled={disabled || busy}>{t('Importar JSON')}</Button>
      <Button startIcon={<FileDownloadOutlined />} onClick={() => open('export')} disabled={disabled || busy || !profiles.length}>{t('Exportar JSON')}</Button>
    </Box>
    <Dialog open={Boolean(mode)} onClose={close} fullWidth maxWidth="sm" className="cv-profile-dialog">
      <DialogTitle>{t(mode === 'export' ? 'Exportar currículums' : 'Importar currículums')}</DialogTitle>
      <DialogContent>
        {mode === 'export' ? <>
          <Typography color="text.secondary" sx={{mb: 2}}>{t('Selecciona uno o varios currículums de {name}.', {name: contextName})}</Typography>
          <FormControlLabel control={<Checkbox checked={selected.length === profiles.length && profiles.length > 0} indeterminate={selected.length > 0 && selected.length < profiles.length} disabled={busy} onChange={event => setSelected(event.target.checked ? profiles.map(p => p.id) : [])} />} label={t('Seleccionar todos')} />
          <Box sx={{display: 'grid'}}>{profiles.map(profile => <FormControlLabel key={profile.id} control={<Checkbox checked={selected.includes(profile.id)} disabled={busy} onChange={event => setSelected(previous => event.target.checked ? [...previous, profile.id] : previous.filter(id => id !== profile.id))} />} label={profile.name} />)}</Box>
        </> : <>
          <Typography color="text.secondary" sx={{mb: 2}}>{t('Abre un archivo JSON exportado por DevsSoft. Se crearán nuevas copias.')}</Typography>
          <Button component="label" variant="outlined" disabled={busy || attempted.current}>{t('Seleccionar archivo JSON')}<input type="file" accept=".json,application/json" hidden aria-label={t('Archivo JSON de currículums')} onChange={readFile} disabled={busy || attempted.current} /></Button>
          {curriculums && <>
            <Typography sx={{mt: 2}}>{t('{count} currículums para importar.', {count: curriculums.length})}</Typography>
            <Box component="ul" sx={{maxHeight: 140, overflow: 'auto', pl: 3}}>{curriculums.map((cv, index) => <Typography component="li" key={index}>{cv.name}</Typography>)}</Box>
            <Typography component="h3" sx={{mt: 2, fontSize: 16, fontWeight: 600}}>{t('¿De quién es este currículum?')}</Typography>
            <Box className="cv-owner-choices" role="group" aria-label={t('Destino de los currículums')}>
              <Button variant={scope === 'self' ? 'contained' : 'outlined'} aria-pressed={scope === 'self'} startIcon={<PersonOutline />} onClick={() => setScope('self')} disabled={busy || attempted.current}>{t('Es mi currículum')}</Button>
              <Button variant={scope === 'other' ? 'contained' : 'outlined'} aria-pressed={scope === 'other'} startIcon={<GroupsOutlined />} onClick={() => setScope('other')} disabled={busy || attempted.current}>{t('Es de otra persona')}</Button>
            </Box>
            {scope === 'self' && <Typography color="text.secondary">{t('Se añadirán a Mis currículums.')}</Typography>}
            {scope === 'other' && <Box sx={{display: 'grid', gap: 2, mt: 2}}>
              <TextField select label={t('Persona de destino')} value={personId} onChange={event => setPersonId(event.target.value)} disabled={busy || attempted.current || loadingPersons} fullWidth>
                <MenuItem value="">{t('Nueva persona')}</MenuItem>{persons.map(person => <MenuItem key={person.id} value={person.id}>{person.name}{persons.filter(p => p.name === person.name).length > 1 ? ` · ${person.id.slice(-8)}` : ''}</MenuItem>)}
              </TextField>
              {!personId && <TextField label={t('Nombre de la persona')} value={personName} onChange={event => setPersonName(event.target.value)} disabled={busy || attempted.current} inputProps={{maxLength: 200}} fullWidth required />}
              {peopleError && <Alert severity="error" action={<Button onClick={() => setPeopleAttempt(value => value + 1)}>{t('Reintentar')}</Button>}>{t(peopleError)}</Alert>}
            </Box>}
          </>}
        </>}
        {error && <Alert severity="error" sx={{mt: 2}}>{t(operationError || error)}</Alert>}
      </DialogContent>
      <DialogActions><Button onClick={close} disabled={busy}>{t('Cancelar')}</Button><Button variant="contained" onClick={submit} disabled={busy || (mode === 'export' ? !selected.length : !curriculums || destinationMissing)}>{t(busy ? 'Procesando…' : error && attempted.current ? 'Reintentar importación' : mode === 'export' ? 'Descargar JSON' : 'Importar currículums')}</Button></DialogActions>
    </Dialog>
  </>;
}
